import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatEuro, formatPct } from '../../lib/format';
import { KPICard } from './KPICard';
import { KPIObjectifCard } from './KPIObjectifCard';
import type { DashboardFiltersState } from './filters';
import { presetToBounds } from './filters';

interface Props {
  filters: DashboardFiltersState;
}

export function KPIGrid({ filters }: Props): JSX.Element {
  const bounds = presetToBounds(filters);
  const params = {
    period_from: bounds.period_from,
    period_to: bounds.period_to,
    user_id: filters.user_id,
  };

  const query = useQuery({
    queryKey: ['dashboard', 'kpis', params],
    queryFn: () => api.dashboard.kpis(params),
    staleTime: 60_000,
  });

  const pipelineValueQuery = useQuery({
    queryKey: ['dashboard', 'pipeline-value', filters.user_id],
    queryFn: () => api.dashboard.pipelineValue({ user_id: filters.user_id }),
    staleTime: 60_000,
  });

  const stockValueQuery = useQuery({
    queryKey: ['dashboard', 'stock-value'],
    queryFn: () => api.dashboard.stockValue(),
    staleTime: 60_000,
  });

  const alertesQuery = useQuery({
    queryKey: ['dashboard', 'alertes-stock'],
    queryFn: () => api.dashboard.alertesStock(),
    staleTime: 60_000,
  });

  const tresorerieQuery = useQuery({
    queryKey: ['dashboard-tresorerie'],
    queryFn: () => api.dashboard.tresorerie(),
    staleTime: 60_000,
  });

  const loading = query.isLoading;
  const data = query.data;
  const pipelineLoading = pipelineValueQuery.isLoading;
  const pipelineData = pipelineValueQuery.data;
  const stockLoading = stockValueQuery.isLoading;
  const stockData = stockValueQuery.data;
  const alertesData = alertesQuery.data;

  const margePct = data?.marge_periode_pct ?? null;
  const margeEur = data?.marge_periode_ht_centimes ?? null;

  let margeValue: string;
  let margeTooltip: string | undefined;
  if (loading) {
    margeValue = '…';
  } else if (margePct === null || margeEur === null) {
    margeValue = '—';
    margeTooltip = 'Marge non calculable sans coût (Phase 3)';
  } else {
    margeValue = `${formatPct(margePct)} · ${formatEuro(margeEur)}`;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KPICard
        label="CA période"
        value={formatEuro(data?.ca_periode_ht_centimes ?? 0)}
        hint="HT, ventes non supprimées"
        loading={loading}
      />
      <KPICard
        label="CA mois en cours"
        value={formatEuro(data?.ca_mois_en_cours_ht_centimes ?? 0)}
        hint="HT, indépendant du filtre période"
        loading={loading}
      />
      <KPICard
        label="Marge brute moyenne"
        value={margeValue}
        hint={margeTooltip === undefined ? 'Pondérée par CA' : undefined}
        tooltip={margeTooltip}
        loading={loading}
      />
      <KPICard
        label="Nb ventes"
        value={loading ? '…' : String(data?.nb_ventes ?? 0)}
        loading={loading}
      />
      <KPICard
        label="Panier moyen"
        value={formatEuro(data?.panier_moyen_ht_centimes ?? null)}
        hint="HT par vente"
        loading={loading}
      />
      <KPICard
        label="Clients actifs"
        value={loading ? '…' : String(data?.nb_clients_actifs ?? 0)}
        hint="Au moins 1 vente"
        loading={loading}
      />
      <KPIObjectifCard
        caAnnuelCentimes={data?.ca_annee_en_cours_ht_centimes ?? 0}
        objectifCentimes={data?.objectif_annuel_ht_centimes ?? 10_000_000}
        loading={loading}
      />
      <KPICard
        label="Pipeline CRM"
        value={pipelineLoading ? '…' : formatEuro(pipelineData?.value_centimes ?? 0)}
        hint={
          pipelineData !== undefined
            ? `${String(pipelineData.nb_contacts)} contact${pipelineData.nb_contacts > 1 ? 's' : ''} en Devis_envoye + Negociation`
            : undefined
        }
        loading={pipelineLoading}
      />
      <KPICard
        label="Valeur stock dispo"
        value={stockLoading ? '…' : formatEuro(stockData?.valeur_stock_centimes ?? 0)}
        hint={
          stockData !== undefined
            ? `${String(stockData.nb_rouleaux_dispo)} rouleau${stockData.nb_rouleaux_dispo > 1 ? 'x' : ''} · ${String(stockData.nb_references_avec_stock)} réf.`
            : undefined
        }
        loading={stockLoading}
      />
      <KPICard
        label="Alertes stock"
        value={
          alertesData === undefined
            ? '…'
            : alertesData.nb_references_sous_seuil === 0
              ? '0'
              : String(alertesData.nb_references_sous_seuil)
        }
        hint={
          alertesData !== undefined && alertesData.nb_references_sous_seuil > 0
            ? `${String(alertesData.nb_references_sous_seuil)} référence${alertesData.nb_references_sous_seuil > 1 ? 's' : ''} sous seuil`
            : 'Aucune alerte'
        }
        loading={alertesQuery.isLoading}
      />
      <KPICard
        label="Dette Global Wood"
        value={
          tresorerieQuery.isLoading
            ? '…'
            : tresorerieQuery.data?.configured !== true
              ? 'Non configuré'
              : formatEuro(tresorerieQuery.data.dette_global_wood_restant_centimes ?? 0)
        }
        hint="Restant à rembourser"
        loading={tresorerieQuery.isLoading}
      />
      <KPICard
        label="Total impayés"
        value={
          tresorerieQuery.isLoading
            ? '…'
            : tresorerieQuery.data?.configured !== true
              ? '—'
              : formatEuro(tresorerieQuery.data.total_impayes_centimes ?? 0)
        }
        hint="Ventes non soldées"
        loading={tresorerieQuery.isLoading}
      />
      <KPICard
        label="Impayés en retard"
        value={
          tresorerieQuery.isLoading
            ? '…'
            : tresorerieQuery.data?.configured !== true
              ? '—'
              : formatEuro(tresorerieQuery.data.total_impayes_en_retard_centimes ?? 0)
        }
        hint={
          (tresorerieQuery.data?.total_impayes_en_retard_centimes ?? 0) > 0
            ? 'Alerte : à relancer'
            : 'Aucun retard'
        }
        loading={tresorerieQuery.isLoading}
      />
      <KPICard
        label="Solde prévisionnel 3M"
        value={
          tresorerieQuery.isLoading
            ? '…'
            : tresorerieQuery.data?.configured !== true
              ? '—'
              : formatEuro(tresorerieQuery.data.solde_previsionnel_3m_centimes ?? 0)
        }
        hint="Cash flow cumulé"
        loading={tresorerieQuery.isLoading}
      />
    </div>
  );
}
